import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { useTranslation } from "react-i18next";
import { RotateCcw, ZoomIn, ZoomOut, Move3d, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { extOf } from "../lib/cad";

// In-memory cache of downloaded CAD file bytes (keyed by original URL). Switching
// between already-loaded files re-parses from memory instead of re-downloading.
const bufferCache = new Map();

export const StlViewer = ({ url, name }) => {
  const { t } = useTranslation();
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let cancelled = false;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf1f5f9);
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);
    camera.position.set(0, 0, 120);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(1, 1, 1);
    scene.add(dir);
    const dir2 = new THREE.DirectionalLight(0xffffff, 0.4);
    dir2.position.set(-1, -1, -1);
    scene.add(dir2);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    const material = new THREE.MeshStandardMaterial({ color: 0x64748b, metalness: 0.15, roughness: 0.55 });

    let object;
    const fitCameraToObject = (obj) => {
      const box = new THREE.Box3().setFromObject(obj);
      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      box.getCenter(center);
      box.getSize(size);
      obj.position.sub(center); // center the object
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const dist = maxDim * 2.2;
      camera.position.set(dist * 0.4, dist * 0.3, dist);
      camera.near = maxDim / 100;
      camera.far = maxDim * 100;
      camera.updateProjectionMatrix();
      controls.target.set(0, 0, 0);
      controls.update();
      stateRef.current.home = camera.position.clone();
    };

    const addGeometry = (geometry) => {
      geometry.computeVertexNormals?.();
      object = new THREE.Mesh(geometry, material);
      scene.add(object);
      fitCameraToObject(object);
    };

    const loadDemo = () => {
      const geo = new THREE.TorusKnotGeometry(22, 7, 160, 24);
      object = new THREE.Mesh(geo, material);
      scene.add(object);
      fitCameraToObject(object);
    };

    if (url) {
      const ext = extOf(name || url);
      const proxied = /firebasestorage|googleapis/.test(url)
        ? `${process.env.REACT_APP_BACKEND_URL}/api/stl-proxy?url=${encodeURIComponent(url)}`
        : url;
      setStatus("loading");
      const parseBuffer = (buffer) => {
        if (ext === "obj") {
          const obj = new OBJLoader().parse(new TextDecoder().decode(buffer));
          obj.traverse((c) => { if (c.isMesh) c.material = material; });
          object = obj;
          scene.add(obj);
          fitCameraToObject(obj);
        } else if (ext === "ply") {
          addGeometry(new PLYLoader().parse(buffer));
        } else {
          addGeometry(new STLLoader().parse(buffer));
        }
      };
      const done = (buffer) => {
        if (cancelled) return;
        try {
          parseBuffer(buffer);
          setStatus("stl");
        } catch {
          loadDemo();
          setStatus("error");
        }
      };
      const cached = bufferCache.get(url);
      if (cached) {
        // Reuse the already-downloaded bytes: near-instant switch between files.
        done(cached);
      } else {
        fetch(proxied)
          .then((r) => { if (!r.ok) throw new Error(`http ${r.status}`); return r.arrayBuffer(); })
          .then((buf) => { bufferCache.set(url, buf); done(buf); })
          .catch(() => { if (!cancelled) { loadDemo(); setStatus("error"); } });
      }
    } else {
      loadDemo();
      setStatus("demo");
    }

    let frame;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);
    stateRef.current = { ...stateRef.current, camera, controls, renderer };

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [url, name]);

  const zoom = (factor) => {
    const { camera, controls } = stateRef.current;
    if (!camera || !controls) return;
    const d = new THREE.Vector3().subVectors(camera.position, controls.target);
    d.multiplyScalar(factor);
    camera.position.copy(controls.target).add(d);
    controls.update();
  };
  const reset = () => {
    const { camera, controls, home } = stateRef.current;
    if (!camera || !controls || !home) return;
    camera.position.copy(home);
    controls.target.set(0, 0, 0);
    controls.update();
  };

  return (
    <div className="relative overflow-hidden rounded-lg border border-border bg-slate-100" data-testid="stl-viewer">
      <div ref={mountRef} className="h-[340px] w-full" />
      {status === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-100/70 backdrop-blur-sm" data-testid="stl-viewer-loading">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
          <span className="text-xs font-medium text-muted-foreground">{t("viewer.loading")}</span>
        </div>
      )}
      <div className="pointer-events-none absolute left-3 top-3 flex max-w-[calc(100%-1.5rem)] items-center gap-1.5 rounded-full bg-background/80 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
        <Move3d className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{status === "stl" ? name || "model" : status === "loading" ? t("viewer.loading") : t("viewer.demoModel")}</span>
      </div>
      <div className="absolute bottom-3 right-3 flex gap-1.5">
        <Button size="icon" variant="secondary" className="h-8 w-8 rounded-full shadow-sm" onClick={() => zoom(0.85)} data-testid="viewer-zoom-in" title={t("viewer.zoomIn")}>
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="secondary" className="h-8 w-8 rounded-full shadow-sm" onClick={() => zoom(1.18)} data-testid="viewer-zoom-out" title={t("viewer.zoomOut")}>
          <ZoomOut className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="secondary" className="h-8 w-8 rounded-full shadow-sm" onClick={reset} data-testid="viewer-reset" title={t("viewer.reset")}>
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
